/**
 * Tracking loader — injects GA4 / Meta Pixel / custom HTML scripts gated by user consent.
 * Listens to consent updates (via custom event 'cookie-consent-update' or storage event)
 * and loads/unloads the corresponding scripts.
 */

const CONSENT_KEY = "truth_or_debt_cookie_consent";
const SCRIPT_ATTR = "data-tracking-injected";

function readConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function removeInjected(group) {
  document.querySelectorAll(`[${SCRIPT_ATTR}="${group}"]`).forEach((el) => el.remove());
}

function injectScriptElement(src, attrs = {}, group) {
  const s = document.createElement("script");
  if (src) s.src = src;
  s.async = true;
  s.setAttribute(SCRIPT_ATTR, group);
  Object.entries(attrs).forEach(([k, v]) => s.setAttribute(k, v));
  document.head.appendChild(s);
  return s;
}

function injectInlineScript(code, group) {
  const s = document.createElement("script");
  s.text = code;
  s.setAttribute(SCRIPT_ATTR, group);
  document.head.appendChild(s);
  return s;
}

/**
 * Inject arbitrary HTML safely — re-executes <script> tags found inside.
 */
function injectRawHtml(html, group) {
  if (!html || !html.trim()) return;
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  Array.from(tmp.childNodes).forEach((node) => {
    if (node.nodeName === "SCRIPT") {
      const ns = document.createElement("script");
      Array.from(node.attributes).forEach((a) => ns.setAttribute(a.name, a.value));
      ns.text = node.textContent || "";
      ns.setAttribute(SCRIPT_ATTR, group);
      document.head.appendChild(ns);
    } else if (node.nodeType === 1) {
      const cloned = node.cloneNode(true);
      cloned.setAttribute(SCRIPT_ATTR, group);
      document.head.appendChild(cloned);
    }
  });
}

function injectGA4(id, group) {
  if (!id || !id.trim()) return;
  // gtag.js loader
  injectScriptElement(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`, {}, group);
  injectInlineScript(
    `window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
window.gtag = window.gtag || gtag;
gtag('js', new Date());
gtag('config', '${id}', { anonymize_ip: true });`,
    group
  );
}

function injectMetaPixel(id, group) {
  if (!id || !id.trim()) return;
  // Safety-hardened init:
  // - `autoConfig:false` disables Meta's automatic DOM scanning (button text, form labels,
  //   click/scroll tracking). Critical for finance/debt sites — prevents leaking Hebrew UI
  //   text like "מחק חוב 50,000₪" that would violate Meta's Sensitive Categories policy.
  // - No custom_data is ever passed to PageView. Custom events (e.g. Lead) are fired
  //   elsewhere also without custom_data — see LandingPage.submit().
  injectInlineScript(
    `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
fbq('set','autoConfig',false,'${id}');
fbq('init','${id}');
fbq('track','PageView');`,
    group
  );
}

/**
 * Force-load Meta Pixel BEFORE the user grants cookie consent.
 * Triggered when the user starts interacting with the lead form — this lets us
 * track high-intent users earlier in the funnel. Safe to call multiple times.
 */
let _pixelForceLoaded = false;
export function forceLoadMetaPixel(codes) {
  if (_pixelForceLoaded) return;
  const camp = codes?.campaign || {};
  const aff = codes?.affiliate || {};
  const fired = (!!camp.meta_pixel_id) || (!!aff.meta_pixel_id);
  if (camp.meta_pixel_id) injectMetaPixel(camp.meta_pixel_id, "campaign-marketing");
  if (aff.meta_pixel_id) injectMetaPixel(aff.meta_pixel_id, "affiliate-marketing");
  if (fired) _pixelForceLoaded = true;
}

/**
 * Programmatically grant all cookies (essential + analytics + marketing).
 * Triggered when the user submits the lead form — at that point they've
 * provided personal details and we treat that as informed consent.
 * Saves consent, updates gtag, and dispatches the standard update event so
 * TrackingLoader injects every remaining tracker (GA4 + custom HTML).
 */
export function grantAllConsent() {
  const payload = {
    essential: true,
    analytics: true,
    marketing: true,
    version: 1,
    ts: Date.now(),
    auto_granted: true,
  };
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(payload));
  } catch (err) {
    console.warn("grantAllConsent: persist failed", err?.message);
  }
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: "consent_update", consent: payload });
    if (typeof window.gtag === "function") {
      window.gtag("consent", "update", {
        ad_storage: "granted",
        ad_user_data: "granted",
        ad_personalization: "granted",
        analytics_storage: "granted",
        security_storage: "granted",
      });
    }
  } catch (err) {
    console.warn("grantAllConsent: gtag failed", err?.message);
  }
  try {
    window.dispatchEvent(new CustomEvent("cookie-consent-update", { detail: payload }));
  } catch (err) {
    console.warn("grantAllConsent: dispatch failed", err?.message);
  }
  return payload;
}

/**
 * Main application function.
 * @param {{campaign?: {ga4_measurement_id, meta_pixel_id, custom_head_code}, affiliate?: {...}}} codes
 */
export function applyTracking(codes) {
  const consent = readConsent();
  // Preserve any pixels already force-loaded — re-injecting would fire a duplicate PageView in Meta.
  const groupsToRemove = ["campaign-analytics", "affiliate-analytics", "campaign-custom", "affiliate-custom"];
  if (!_pixelForceLoaded) {
    groupsToRemove.push("campaign-marketing", "affiliate-marketing");
  }
  groupsToRemove.forEach(removeInjected);

  if (!consent) return;
  const camp = codes?.campaign || {};
  const aff = codes?.affiliate || {};

  if (consent.analytics) {
    if (camp.ga4_measurement_id) injectGA4(camp.ga4_measurement_id, "campaign-analytics");
    if (aff.ga4_measurement_id) injectGA4(aff.ga4_measurement_id, "affiliate-analytics");
  }

  if (consent.marketing) {
    // Only inject if not already force-loaded earlier (avoids duplicate PageView)
    if (!_pixelForceLoaded) {
      if (camp.meta_pixel_id) injectMetaPixel(camp.meta_pixel_id, "campaign-marketing");
      if (aff.meta_pixel_id) injectMetaPixel(aff.meta_pixel_id, "affiliate-marketing");
    }
    // Custom head code may contain remarketing snippets — only load with marketing consent
    if (camp.custom_head_code) injectRawHtml(camp.custom_head_code, "campaign-custom");
    if (aff.custom_head_code) injectRawHtml(aff.custom_head_code, "affiliate-custom");
  }
}
